import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../utils/api';
import toast from 'react-hot-toast';
import { Link2, Plus, Trash2, ArrowRight } from 'lucide-react';

interface Port {
  id: string;
  name: string;
  port_type: string;
  rear_port_id: string | null;
}

interface PatchPanelMappingProps {
  equipmentId: string;
  equipmentName: string;
}

export function PatchPanelMapping({ equipmentId, equipmentName }: PatchPanelMappingProps) {
  const queryClient = useQueryClient();
  const [mappingDraft, setMappingDraft] = useState<{ frontId: string; rearId: string } | null>(null);

  const { data: ports = [], isLoading } = useQuery<Port[]>({
    queryKey: ['ports', equipmentId],
    queryFn: () => api.get(`/api/equipment/${equipmentId}/ports`).then(r => r.data),
  });

  const frontPorts = ports.filter(p => p.port_type === 'front' || (!p.rear_port_id && p.port_type !== 'rear'));
  const rearPorts = ports.filter(p => p.port_type === 'rear');

  const setMapping = useMutation({
    mutationFn: ({ portId, rearPortId }: { portId: string; rearPortId: string | null }) =>
      api.patch(`/api/equipment/ports/${portId}`, { rear_port_id: rearPortId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ports', equipmentId] });
      toast.success('Маппинг портов обновлён');
      setMappingDraft(null);
    },
    onError: () => toast.error('Ошибка обновления маппинга'),
  });

  const clearMapping = (portId: string) => {
    setMapping.mutate({ portId, rearPortId: null });
  };

  const applyMapping = () => {
    if (!mappingDraft) return;
    setMapping.mutate({ portId: mappingDraft.frontId, rearPortId: mappingDraft.rearId });
  };

  if (isLoading) {
    return <div className="text-xs animate-pulse py-4" style={{ color: 'var(--text-muted)' }}>Загрузка портов...</div>;
  }

  if (rearPorts.length === 0) {
    return (
      <div
        className="text-xs py-3 text-center border border-dashed rounded-sm"
        style={{ color: 'var(--text-muted)', borderColor: 'var(--border-color)' }}
      >
        Нет задних портов у <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{equipmentName}</span>.<br/>
        Добавьте порты с типом «rear» для настройки маппинга.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-2">
        <Link2 className="w-3.5 h-3.5" style={{ color: 'var(--accent)' }} />
        <span className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
          Маппинг Front→Rear ({equipmentName})
        </span>
      </div>

      {/* Existing mappings */}
      <div className="space-y-1.5">
        {frontPorts.map(fp => {
          const mapped = fp.rear_port_id ? rearPorts.find(r => r.id === fp.rear_port_id) : null;
          return (
            <div
              key={fp.id}
              className="flex items-center gap-2 p-2 rounded-sm border text-[11px] font-mono"
              style={{
                background: mapped ? 'rgba(168, 85, 247, 0.1)' : 'var(--bg-panel)',
                borderColor: mapped ? 'rgba(168, 85, 247, 0.3)' : 'var(--border-color)',
              }}
            >
              <span className="font-bold w-24 truncate" style={{ color: 'var(--accent)' }} title={fp.name}>
                {fp.name}
              </span>
              <ArrowRight className="w-3 h-3 shrink-0" style={{ color: 'var(--text-muted)' }} />
              {mapped ? (
                <>
                  <span className="font-bold flex-1 truncate" style={{ color: '#c084fc' }}>{mapped.name}</span>
                  <button
                    onClick={() => clearMapping(fp.id)}
                    className="transition ml-auto shrink-0 hover:text-red-400"
                    style={{ color: 'var(--text-muted)' }}
                    title="Удалить маппинг"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </>
              ) : (
                <span className="flex-1 italic" style={{ color: 'var(--text-muted)' }}>не задан</span>
              )}
            </div>
          );
        })}
      </div>

      {/* Add new mapping */}
      <div className="border-t pt-3" style={{ borderColor: 'var(--border-color)' }}>
        <div className="text-[10px] uppercase mb-2 font-bold" style={{ color: 'var(--text-muted)' }}>
          Новый маппинг
        </div>
        <div className="flex gap-2 items-center">
          <select
            className="flex-1 rounded-sm px-2 py-1.5 text-xs focus:outline-none"
            style={{
              background: 'var(--bg-input)',
              border: '1px solid var(--border-color)',
              color: 'var(--text-primary)',
            }}
            value={mappingDraft?.frontId || ''}
            onChange={e => setMappingDraft(prev => ({ frontId: e.target.value, rearId: prev?.rearId || '' }))}
          >
            <option value="">Front порт...</option>
            {frontPorts.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <ArrowRight className="w-4 h-4 shrink-0" style={{ color: 'var(--text-muted)' }} />
          <select
            className="flex-1 rounded-sm px-2 py-1.5 text-xs focus:outline-none"
            style={{
              background: 'var(--bg-input)',
              border: '1px solid var(--border-color)',
              color: 'var(--text-primary)',
            }}
            value={mappingDraft?.rearId || ''}
            onChange={e => setMappingDraft(prev => ({ frontId: prev?.frontId || '', rearId: e.target.value }))}
          >
            <option value="">Rear порт...</option>
            {rearPorts.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <button
            onClick={applyMapping}
            disabled={!mappingDraft?.frontId || !mappingDraft?.rearId || setMapping.isPending}
            className="shrink-0 p-1.5 rounded-sm disabled:opacity-40 disabled:cursor-not-allowed transition"
            style={{
              background: 'var(--accent)',
              color: 'var(--text-on-accent, #ffffff)',
            }}
            title="Применить маппинг"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
